import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStable } from "../context/StableContext";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import HorseImage from "../components/HorseImage";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Plus, Search, Loader2, Link2, LayoutGrid, List, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const birthYear = (dob) => (dob ? String(dob).slice(0, 4) : null);
const ageYears = (dob) => {
  if (!dob) return null;
  const b = new Date(String(dob).slice(0, 10) + "T00:00:00");
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
  return a >= 0 ? a : null;
};

export default function Horses() {
  const { current, perms } = useStable();
  const { user, updatePreferences } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [horses, setHorses] = useState(null);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const category = params.get("category") || "all";
  const presence = params.get("presence") || "all";
  const operational = params.get("operational") || "all";

  const [view, setView] = useState(
    () => localStorage.getItem("equiro_horses_view") || user?.preferences?.horses_view || "cards"
  );
  useEffect(() => {
    const pref = user?.preferences?.horses_view;
    if (pref && pref !== view) { setView(pref); localStorage.setItem("equiro_horses_view", pref); }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeView = (v) => {
    setView(v);
    localStorage.setItem("equiro_horses_view", v);
    updatePreferences({ horses_view: v });
  };

  const load = useCallback(() => {
    if (!current) return;
    const q = {};
    if (search) q.search = search;
    if (category !== "all") q.category = category;
    if (presence !== "all") q.presence = presence;
    if (operational !== "all") q.operational = operational;
    setHorses(null);
    api.get(`/stables/${current.id}/horses`, { params: q }).then((r) => setHorses(r.data));
  }, [current, search, category, presence, operational]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (current) api.get(`/stables/${current.id}/horse-categories`).then((r) => setCats((r.data || []).filter((c) => !c.archived)));
  }, [current]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    v === "all" ? p.delete(k) : p.set(k, v);
    setParams(p);
  };

  return (
    <div className="space-y-6 eq-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono-label text-[#8C532B] text-xs">Registro</p>
          <h1 className="font-display text-2xl lg:text-3xl font-bold text-[#1B3B2B]">Cavalli</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-[#E5DEC9] bg-white overflow-hidden" data-testid="horses-view-toggle">
            <button data-testid="view-cards" onClick={() => changeView("cards")} title="Schede"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm ${view === "cards" ? "bg-[#1B3B2B] text-white" : "text-[#5F6B64]"}`}>
              <LayoutGrid className="h-4 w-4" /> <span className="hidden lg:inline">Schede</span>
            </button>
            <button data-testid="view-compact" onClick={() => changeView("compact")} title="Compatto"
              className={`flex items-center gap-1.5 px-3 py-2 text-sm ${view === "compact" ? "bg-[#1B3B2B] text-white" : "text-[#5F6B64]"}`}>
              <List className="h-4 w-4" /> <span className="hidden lg:inline">Compatto</span>
            </button>
          </div>
          {perms.canWrite && (
            <Button data-testid="add-horse-btn" onClick={() => navigate("/cavalli/nuovo")}
              className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              <Plus className="h-4 w-4 mr-2" /> <span className="hidden sm:inline">Aggiungi cavallo</span><span className="sm:hidden">Aggiungi</span>
            </Button>
          )}
          {perms.isOwner && <RequestHorseDialog stableId={current.id} />}
        </div>
      </div>

      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C532B]" />
          <Input data-testid="horse-search" placeholder="Cerca per nome, microchip, proprietario…"
            value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
        </div>
        <div className="flex gap-3">
          <Select value={category} onValueChange={(v) => setParam("category", v)}>
            <SelectTrigger className="flex-1 min-w-0 bg-white" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={presence} onValueChange={(v) => setParam("presence", v)}>
            <SelectTrigger className="flex-1 min-w-0 bg-white" data-testid="filter-presence"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le presenze</SelectItem>
              <SelectItem value="presente">Presente</SelectItem>
              <SelectItem value="assente_temporaneo">Temporaneamente assente</SelectItem>
            </SelectContent>
          </Select>
          <Select value={operational} onValueChange={(v) => setParam("operational", v)}>
            <SelectTrigger className="flex-1 min-w-0 bg-white" data-testid="filter-operational"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="attivo">Attivi</SelectItem>
              <SelectItem value="temp_indisponibile">Temp. indisponibili</SelectItem>
              <SelectItem value="ritirato">Ritirati</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {!horses ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>
      ) : horses.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E5DEC9] py-16 text-center text-[#5F6B64]">
          Nessun cavallo trovato.
        </div>
      ) : view === "compact" ? (
        <div className="bg-white rounded-xl border border-[#E5DEC9] divide-y divide-[#F0EAE1] overflow-hidden" data-testid="horses-compact-list">
          {horses.map((h) => {
            const yr = birthYear(h.dob);
            const ag = ageYears(h.dob);
            const meta = [h.breed, yr ? `${yr}${ag != null ? ` · ${ag} anni` : ""}` : null, h.category].filter(Boolean).join(" · ");
            return (
              <button key={h.id} data-testid={`horse-row-${h.id}`} onClick={() => navigate(`/cavalli/${h.id}`)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#FCFAF6] active:bg-[#FCFAF6] transition-colors">
                <div className="h-11 w-11 rounded-lg overflow-hidden bg-[#F0EAE1] shrink-0">
                  <HorseImage photo={h.photo} alt={h.display_name} className="h-full w-full" fallbackClass="h-full w-full" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold text-[#1B3B2B] truncate leading-tight">{h.display_name}</p>
                  <p className="text-xs text-[#5F6B64] truncate">{meta || "—"}</p>
                </div>
                {(h.operational_status && h.operational_status !== "attivo") && <span data-testid={`op-dot-${h.id}`} className="h-2.5 w-2.5 rounded-full shrink-0" title={h.operational_status === "ritirato" ? "Ritirato" : "Temp. indisponibile"} style={{ background: h.operational_status === "ritirato" ? "#2563A8" : "#E08A00" }} />}
                {h.presence_status !== "presente" && <StatusBadge status={h.presence_status} />}
                <ChevronRight className="h-4 w-4 text-[#C9BDA0] shrink-0" />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {horses.map((h) => (
            <button key={h.id} data-testid={`horse-card-${h.id}`} onClick={() => navigate(`/cavalli/${h.id}`)}
              className="group text-left bg-white rounded-xl border border-[#E5DEC9] overflow-hidden hover:shadow-md hover:border-[#8C532B] transition-all">
              <div className="h-44 bg-[#F0EAE1] overflow-hidden">
                <HorseImage photo={h.photo} alt={h.display_name}
                  className="h-full w-full group-hover:scale-105 transition-transform duration-500" fallbackClass="h-full w-full" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-display font-bold text-lg text-[#1B3B2B] truncate">{h.display_name}</h3>
                    <p className="text-xs text-[#5F6B64] truncate">{h.official_name || "—"}</p>
                  </div>
                  {(h.operational_status && h.operational_status !== "attivo") && <span data-testid={`op-badge-card-${h.id}`} className="text-[10px] font-medium rounded-full px-2 py-0.5" style={{ background: h.operational_status === "ritirato" ? "#E8F0FA" : "#FCF3E2", color: h.operational_status === "ritirato" ? "#1B4B82" : "#8A5A00" }}>{h.operational_status === "ritirato" ? "Ritirato" : "Indisponibile"}</span>}
                  {h.presence_status !== "presente" && <StatusBadge status={h.presence_status} />}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {h.category && <span className="text-[10px] font-mono-label rounded-full px-2 py-0.5 bg-[#F0EAE1] text-[#8C532B]">{h.category}</span>}
                  {h.breed && <span className="text-xs text-[#5F6B64]">{h.breed}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RequestHorseDialog({ stableId }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("microchip");
  const [microchip, setMicrochip] = useState("");
  const [horseName, setHorseName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mode === "microchip" ? { microchip } : { horse_name: horseName };
      const { data } = await api.post(`/stables/${stableId}/horse-requests`, payload);
      toast.success(`Richiesta inviata per "${data.horse_name}". In attesa di approvazione del Gestore.`);
      setOpen(false); setMicrochip(""); setHorseName("");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" data-testid="request-horse-btn" onClick={() => setOpen(true)}
        className="border-[#8C532B] text-[#8C532B] hover:bg-[#F0EAE1]">
        <Link2 className="h-4 w-4 mr-2" /> <span className="hidden sm:inline">Richiedi associazione a un cavallo</span><span className="sm:hidden">Richiedi</span>
      </Button>
      <DialogContent data-testid="request-horse-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Richiedi associazione a un cavallo</DialogTitle>
          <DialogDescription>
            Identifica il cavallo tramite microchip esatto oppure nome. La richiesta sarà approvata dal Gestore prima di darti accesso.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="request-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="microchip">Cerca per microchip esatto</SelectItem>
              <SelectItem value="name">Cerca per nome cavallo</SelectItem>
            </SelectContent>
          </Select>
          {mode === "microchip" ? (
            <div>
              <Label>Microchip</Label>
              <Input required className="mt-1.5" data-testid="request-microchip" value={microchip}
                onChange={(e) => setMicrochip(e.target.value)} placeholder="Es. 380271234567890" />
            </div>
          ) : (
            <div>
              <Label>Nome del cavallo</Label>
              <Input required className="mt-1.5" data-testid="request-horse-name" value={horseName}
                onChange={(e) => setHorseName(e.target.value)} placeholder="Nome esatto del cavallo" />
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="request-submit-btn" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Invia richiesta
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
